import { useProgramme } from "@components/__templates__/ProgrammeDetailsPage/ProgrammeDetailsProvider";
import Breadcrumb from "@components/BreadcrumbSocialShare/BreadcrumbSocialShareWrapper";
import SiteHeader from "@components/C01_SiteHeader/C01_SiteHeader";
import Footer from "@components/C02_Footer/C02_Footer";
import ProgrammeComparisonOverlay from "@components/C242_ComparisonOverlay/C242_ComparisonOverlay";
import EMBAGGlobalHeader from "@components/EMBAGGlobalHeader/EMBAGGlobalHeader";
import NotificationBanner from "@components/NotificationBanner/NotificationBanner";
import StructuredData from "@components/StructuredData/StructuredData";
import SubscribeThink from "@components/SubscribeThink/SubscribeThink";
import { Templates } from "@customTypes/Templates";
import { useSettings } from "@utilities/context/settings";
import {
  generateNoFollowValue,
  generateNoIndexValue,
} from "@utilities/noindex-nofollow";
import setGADataLayer from "@utilities/setGADataLayer";
import {
  getGlobalTrackingModel,
  getTrackingModel,
} from "@utilities/trackingModels";
import { SubNavLinkProvider } from "context/subNavLinks";
import uniq from "lodash/uniq";
import Head from "next/head";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import { ModalProvider } from "react-modal-hook";
import { ExistingUserEmailProvider } from "../../context/existingUserEmail";
import { NavigationStateProvider } from "../../context/navigationContext";
import { ProgrammeProvider } from "../../context/programmeComparison";
import { User, UserContext } from "../../context/user";
import { LayoutProps, MetaDataProps } from "./Layout.interfaces";
import passWebsiteTracking from "./passWebsiteTracking";
import useBreadcrumbs from "./useBreadcrumbs";
import useConversionTracking from "./useConversionTracking";

export const Layout = ({
  globalContent,
  slug,
  title,
  pageMeta,
  children,
  headerImage,
  coveoMetadata,
  subNavLinks,
  tags,
}: LayoutProps) => {
  const { siteSettings } = useSettings();

  const defaultPostfix = "London Business School";
  const metaTitle = pageMeta?.title || title;
  const pageMetaTitle = metaTitle?.includes(defaultPostfix)
    ? metaTitle
    : `${metaTitle} | ${defaultPostfix}`;

  const taxonomyCollection = uniq(
    Object.values(tags)
      ?.filter((t) => t?.toLowerCase()?.startsWith("topics:"))
      ?.map((t) => t?.replaceAll(/^topics:/gim, ""))
  );
  const metadataObj: MetaDataProps = {
    title: pageMetaTitle,
    description: pageMeta?.pageDescription || "",
    keywords: pageMeta?.keywords || null,
    googleSiteVerification: pageMeta?.googleSiteVerification,
    image: headerImage || siteSettings?.defaultSeoImage?.url,
    index: generateNoIndexValue(pageMeta?.doNotIndex),
    follow: generateNoFollowValue(pageMeta?.hideFromSearch),
  };

  const schemaData = {
    "@type": "Organization",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": slug,
    },
    name: siteSettings?.siteName,
    description: siteSettings?.businessDescription,
    logo: siteSettings?.logo?.url,
    url: process.env.NEXT_PUBLIC_URL,
    telephone: siteSettings?.telephoneNumber,
    sameAs: siteSettings?.referenceWebsites,
    address: {
      "@type": "PostalAddress",
      streetAddress: siteSettings?.address,
      addressLocality: siteSettings?.city,
      postalCode: siteSettings?.postcode,
      addressCountry: "GB",
    },
    image:
      "https://images.london.edu/hxo16fanegqh/4a13040b-022b-4e04-95b5-d2bfb9cf9be4-asset/16dfbe278dad764091f7c14d85d5f7b3/about-campus-london-hero-486x486.jpg",
  };

  const router = useRouter();
  const { user, isUserLoading } = useContext(UserContext);
  const { programmeTypeCode } = useProgramme();

  let isEMBAG = false;
  if (router?.query?.slug) {
    isEMBAG = router?.query?.slug[0] === "embag";
  }
  const [viewportMeta, setViewportMeta] = useState(
    "initial-scale=1.0, width=device-width"
  );

  useEffect(() => {
    if (navigator && window) {
      if (
        navigator.userAgent.match(/iPad/i) ||
        (window.outerWidth > 768 && window.outerWidth < 1024)
      ) {
        setViewportMeta("width=1024");
      }
    }
  }, []);

  useConversionTracking();
  passWebsiteTracking();

  const isWindow = typeof window === "object";
  const urlParams = isWindow
    ? new URLSearchParams(window.location.search)
    : null;
  const referPage = urlParams ? urlParams.get("referPage") : "";
  const breadcrumbs = useBreadcrumbs(referPage, globalContent);
  const hasBreadcrumbs = globalContent?.siteHeader?.displayBreadcrumbs;
  const lastPageLoadKeyRef = useRef<string | null>(null);

  const [userCopy, setUserCopy] = useState<User | undefined>();
  useEffect(() => {
    if (isUserLoading) return;

    if (JSON.stringify(userCopy) !== JSON.stringify(user) && user !== null) {
      setGADataLayer(getGlobalTrackingModel(user, metadataObj.title));
      // Only send GA page_load once per page/user/programme combination
      const pageLoadKey = JSON.stringify({
        title: metadataObj?.title || "",
        programmeCode: programmeTypeCode || "",
        userId: user?.lbsNumber || "logged-out",
      });
      if (lastPageLoadKeyRef.current !== pageLoadKey) {
        setGADataLayer(getTrackingModel(user ?? null, programmeTypeCode));
        lastPageLoadKeyRef.current = pageLoadKey;
      }

      setUserCopy(user);
    }
  }, [user, isUserLoading]);

  const [metObjCopy, setMetObjCopy] = useState<MetaDataProps | undefined>();
  useEffect(() => {
    if (isUserLoading) return;

    if (JSON.stringify(metObjCopy) !== JSON.stringify(metadataObj)) {
      setGADataLayer(getGlobalTrackingModel(user, metadataObj.title));
      // Only send GA page_load once per page/user/programme combination
      const pageLoadKey = JSON.stringify({
        title: metadataObj?.title || "",
        programmeCode: programmeTypeCode || "",
        userId: user?.lbsNumber || "logged-out",
      });
      if (lastPageLoadKeyRef.current !== pageLoadKey) {
        setGADataLayer(getTrackingModel(user ?? null, programmeTypeCode));
        lastPageLoadKeyRef.current = pageLoadKey;
      }

      setMetObjCopy(metadataObj);
    }
  }, [metadataObj, isUserLoading]);

  const { loggedOut } = router.query;
  const isLoggedIn = Boolean(user);
  const initialShowMessage = loggedOut === "true" && !isLoggedIn;
  const [showLogoutMessage, setShowLogoutMessage] =
    useState(initialShowMessage);

  useEffect(() => {
    if (loggedOut === "true" && !isLoggedIn && !showLogoutMessage) {
      setShowLogoutMessage(true);

      // Remove query param after displaying message
      const newQuery = { ...router.query };
      delete newQuery.loggedOut;
      router.replace(
        { pathname: router.pathname, query: newQuery },
        undefined,
        { shallow: true }
      );
    }
  }, [loggedOut, isLoggedIn, router, showLogoutMessage]);

  // Hide message after 10 seconds
  useEffect(() => {
    if (!showLogoutMessage) return;

    const timerId = setTimeout(() => {
      setShowLogoutMessage(false);
    }, 10000);

    return () => clearTimeout(timerId);
  }, [showLogoutMessage]);

  const siteRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <Head>
        <title>{metadataObj.title}</title>
        <meta name="description" content={metadataObj.description} />

        <meta charSet="utf-8" />
        <meta name="viewport" content={viewportMeta} />
        <meta name="theme-color" content="#f3f3f3" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="canonical" href={slug} />
        <meta key="twitter:card" name="twitter:card" content="summary" />
        <meta
          key="twitter:site"
          name="twitter:site"
          content={siteSettings?.twitterHandle || "@LBS"}
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={metadataObj.title}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={metadataObj.description}
        />
        <meta
          key="twitter:creator"
          name="twitter:creator"
          content={siteSettings?.twitterHandle || "@LBS"}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={`${metadataObj.image}?w=1200&h=1200`}
        />
        <meta key="og:title" property="og:title" content={metadataObj.title} />
        <meta key="og:type" property="og:type" content="article" />
        <meta
          key="og:description"
          property="og:description"
          content={metadataObj.description}
        />
        <meta
          key="og:site_name"
          property="og:site_name"
          content={siteSettings?.siteName || "London Business School"}
        />
        <meta key="og:locale" property="og:locale" content="en_GB" />
        <meta key="og:url" property="og:url" content={`${slug}`} />
        <meta
          key="og:image"
          property="og:image"
          content={`https:${metadataObj.image}?w=1200&h=600`}
        />

        {metadataObj.keywords && (
          <meta
            key="keywords"
            name="keywords"
            content={metadataObj.keywords.join(", ")}
          />
        )}

        <meta
          name="robots"
          content={[metadataObj.index, metadataObj.follow].join(", ")}
        />

        {metadataObj.googleSiteVerification ? (
          <meta
            name="google-site-verification"
            content={metadataObj.googleSiteVerification}
          />
        ) : null}

        <link
          rel="shortcut icon"
          href="/favicon/favicon.ico"
          type="image/x-icon"
        />
        <link rel="icon" href="/favicon/favicon.ico" type="image/x-icon" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon/favicon-16x16.png"
        />

        {coveoMetadata?.templateName && (
          <meta
            name="contenttypename"
            content={Templates[coveoMetadata.templateName]}
          />
        )}
        {coveoMetadata?.templateName && (
          <meta name="contenttypeid" content={coveoMetadata.templateName} />
        )}

        <meta name="pagetitle" content={metadataObj.title} />
        <meta name="pagedescription" content={metadataObj.description} />
        {taxonomyCollection.length && (
          <meta name="taxonomytopics" content={taxonomyCollection.join(";")} />
        )}

        {coveoMetadata &&
          coveoMetadata.templateName === "publicationDetail" && (
            <>
              <meta
                name="publicationauthorslist"
                content={coveoMetadata?.fields?.publicationAuthors}
              />
              <meta
                name="publicationsubjectareaslist"
                content={coveoMetadata?.fields?.publicationSubjectAreasList}
              />
              <meta
                name="publicationyear"
                content={coveoMetadata?.fields?.publicationYear}
              />
              <meta
                name="publicationresearchcenternamelist"
                content={
                  coveoMetadata?.fields?.publicationResearchCenterNameList
                }
              />
            </>
          )}
        <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
      </Head>

      <StructuredData id="siteMeta" schemaData={schemaData} />

      <div
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {showLogoutMessage && "You have been successfully logged out."}
      </div>

      <div className="site" ref={siteRef}>
        {globalContent.siteHeader && !isEMBAG && (
          <NavigationStateProvider>
            <SiteHeader
              // @ts-expect-error - type mistmatch
              content={globalContent.siteHeader}
              siteRef={siteRef}
            />
          </NavigationStateProvider>
        )}

        {isEMBAG && <EMBAGGlobalHeader />}

        <main>
          <NotificationBanner />
          {!isEMBAG && hasBreadcrumbs && (
            <Breadcrumb slug={slug} title={title} breadcrumbs={breadcrumbs} />
          )}
          <ExistingUserEmailProvider>
            <SubNavLinkProvider subNavLinks={subNavLinks}>
              <ProgrammeProvider>
                <ModalProvider>{children}</ModalProvider>
                <ProgrammeComparisonOverlay />
              </ProgrammeProvider>
            </SubNavLinkProvider>
          </ExistingUserEmailProvider>
          {globalContent?.subscribeThink && (
            <SubscribeThink content={globalContent.subscribeThink} />
          )}
        </main>

        {globalContent?.footer && !isEMBAG && (
          <Footer content={globalContent.footer} />
        )}
      </div>
    </>
  );
};
export default Layout;
